import { IIndicationFormat, IPartInfo, ITooth, TDentalArch } from 'lib/types/clinic/projectTypes';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useShallowAppSelector } from 'store/hooks';
import { useCreateProjectIndicationStore } from 'store/local/projectCreateindication';
import useImmerInput from '../useImmerInput';
import { useCreateProjectInfoStore } from 'store/local/projectCreateinfo';
import { ProjectActions } from 'store/actionCreators';
import { useParams } from 'react-router-dom';
import { Tooth } from 'lib/types/projectTypes';
import { antagonistMapper } from 'lib/clinic/mapper';
import { useProjectDetailStore } from 'store/local/projectDetail';
import { overlappingArrayElements } from 'lib/library';
import { getJawByToothNumber } from 'lib/clinic/library';

export const useCreateProjectTeeth = () => {
  // SECTION: declare server state
  const {
    projectData,
    editProjectSuccess,
    indicationFormatData,
    indicationInfoData,
    implantLibrarySuccess,
    implantLibraryData,
    loadedSource,
  } = useShallowAppSelector(state => ({
    projectData: state.project.project.data,
    editProjectSuccess: state.project.editProject.success,
    indicationFormatData: state.util.indicationFormat.data?.indicationFormat,
    indicationInfoData: state.util.indicationInfo.data?.indicationInfo,
    implantLibrarySuccess: state.util.implantLibrary.success,
    implantLibraryData: state.util.implantLibrary.data,
    loadedSource: state.app.loadedSource,
  }));

  // SECTION: declare local state
  const {
    setIndicationTab,
    setOptionTab,
    numbering,
    setNumbering,
    teeth,
    setTeeth,
    tooth,
    setTooth,
    bridge,
    setBridge,
    copyData,
    setCopyData,
    setIndicationAlias,
    setMaterialAlias,
    setIsTypeMore,
    setIsHasData,
    implantLibrary,
    setImplantLibraryData,
    pimNormalSet,
    reset: createProjectIndicationStoreReset,
  } = useCreateProjectIndicationStore();

  const {
    teeth: detailTeeth,
    bridge: detailBridge,
    numbering: detailNumbering,
    isPhotogrammetry,
  } = useProjectDetailStore();

  const { setIsIndicationOpen } = useCreateProjectInfoStore();

  // SECTION: declare variable
  const { pcode: projectCode } = useParams() as { pcode: string };
  const toothSvgRef = useRef<HTMLInputElement | null>(null);
  const teethContextActions = useImmerInput({
    copy: {
      active: false,
      hidden: false,
    },
    paste: {
      active: false,
      hidden: false,
    },
    delete: {
      active: false,
      hidden: false,
    },
  });
  const isValidCreateProject = useMemo(() => {
    return (
      !teeth.some(tooth => tooth.pimType === 'normal' && !tooth.implantLibrary) &&
      pimNormalSet.size !== 0
    );
  }, [teeth]);
  const indicationFormat: IIndicationFormat[] = indicationFormatData?.indication;
  const [isCreatedOnlineProject, setIsCreatedOnlineProject] = useState(false);
  const projectIndication = useMemo(() => {
    if (!projectData) return;

    if (projectData) {
      setIsCreatedOnlineProject(
        loadedSource === 'local' && projectData?.projectInfo?.offlineYn === 0,
      );

      return projectData?.projectInfo?.indication;
    }
  }, [projectData]);

  // SECTION: declare state
  // useEffect(() => {
  //   if (isPhotogrammetry) return;
  //   handleIndicationInit();
  // }, []);
  // useEffect(() => {
  //   console.log('[kkw] projectIndication ______', projectIndication);

  //   if (!!projectIndication) {
  //     const newTeeth = projectIndication?.teeth?.reduce(
  //       (acc: Tooth[], curr: Tooth, index: number) => {
  //         return acc.concat([{ ...curr, ...{ hasData: true } }]);
  //       },
  //       [],
  //     );

  //     setNumbering(projectIndication?.notation);
  //     setTeeth([...newTeeth]);
  //     setBridge([...projectIndication?.bridgeList]);
  //   }
  // }, [projectIndication]);

  useEffect(() => {
    if (editProjectSuccess) {
      setIsIndicationOpen(false);
      createProjectIndicationStoreReset();
      if (loadedSource === 'local') {
        ProjectActions.fetch_project_request({
          channel: 'getProjectDetail',
          data: { projectCode: projectCode },
        });
      }
    }
  }, [editProjectSuccess]);

  useEffect(() => {
    if (!implantLibraryData) return;

    setImplantLibraryData(implantLibraryData.data);
  }, [implantLibraryData]);

  // SECTION: declare function
  // const handleIndicationInit = () => {
  //   const newTeeth = detailTeeth?.reduce((acc: Tooth[], curr: Tooth, index: number) => {
  //     return acc.concat([{ ...curr, ...{ hasData: true } }]);
  //   }, []);

  //   setTeeth(newTeeth);
  //   setBridge(detailBridge);
  //   setNumbering(detailNumbering);
  // };

  const handleToggleTooth = useCallback(
    (toothNumber: number) => {
      setIndicationTab(0);
      setOptionTab(0);

      const currentTooth = teeth.find((tooth: ITooth) => tooth.number === toothNumber);

      if (!!currentTooth) {
        setTooth({ ...currentTooth, hasData: true });
        setIndicationAlias(currentTooth?.indicationAlias || '');
        setMaterialAlias(currentTooth?.materialAlias || '');
        setIsHasData(true);

        const defaultShow = !!indicationInfoData.partList.find(
          (part: IPartInfo) => part.idx === currentTooth?.indicationIdx,
        )?.defaultShow;

        setIsTypeMore(!defaultShow);
      } else {
        setTooth({ number: toothNumber, hasData: false });
        setIndicationAlias('');
        setMaterialAlias('');
        setIsHasData(false);
      }
    },
    [indicationInfoData, teeth, tooth],
  );

  const handleToggleToothPhotogrammetry = useCallback(
    (toothNumber: number) => {
      setIndicationTab(1);
      setOptionTab(0);

      const currentTooth = teeth.find((tooth: ITooth) => tooth.number === toothNumber);

      if (!!currentTooth) {
        setTooth({ ...currentTooth, hasData: true });
        setIndicationAlias(currentTooth?.indicationAlias || '');
        setMaterialAlias(currentTooth?.materialAlias || '');
        setIsHasData(true);
      } else {
        let newTooth: ITooth = {
          number: toothNumber,
          indicationIdx: '1',
          material: '2',
          materialAlias: 'ZI',
          implantType: 9,
          implantAbutmentMaterialIdx: 1,
          implantScanbodyScanIdx: 1,
          preparationType: 1,
          reconstructionType: '1',
          materialShade: 0,
          isLast: true,
          description: '',
          default: null,
          advanced: null,
          hasData: true,
          hasPim: true,
          pimType: 'normal',
          color: '#AE29B5',
          indicationAlias: 'ATCR',
        };
        if (teeth?.length > 0) {
          if (getJawByToothNumber(toothNumber) !== 'none') {
            const pimType = pimNormalSet.has(getJawByToothNumber(toothNumber) as TDentalArch)
              ? 'normal'
              : 'scanbody';

            const toothOption = {
              default: {
                ...teeth[0].default,
                situScan: { value: pimType === 'normal' ? 1 : 0, isSavedLast: false },
              },
              advanced: teeth[0].advanced,
            };

            newTooth = {
              ...newTooth,
              ...toothOption,
              pimType,
            };
            setIsHasData(true);
          }
        } else {
          setIsHasData(false);
        }

        setTooth(newTooth);
        setIndicationAlias('ATCR');
        setMaterialAlias('ZI');
        setTeeth(overlappingArrayElements({ list: teeth, value: newTooth, condition: 'number' }));
      }
    },
    [teeth, tooth],
  );

  // useEffect(() => {
  //   console.log('[kkw] teeth ______', teeth);
  // }, [teeth]);
  // useEffect(() => {
  //   console.log('[kkw] tooth ______', tooth);
  // }, [tooth]);

  const handleChangeToothValue = useCallback((data: object | any) => {}, []);

  const handleNumberingClick = (numbering: number) => {
    setNumbering(numbering);
  };

  const handleSaveIndication = () => {
    const newTeeth = teeth.map(tooth => {
      if (isPhotogrammetry) {
        return { ...tooth, implantLibraryIdx: tooth?.implantLibrary?.implantLibraryIdx };
      } else {
        return { ...tooth };
      }
    });

    const submitData = {
      indication: {
        teeth: newTeeth,
        bridge: bridge,
        notation: numbering,
        scanmodeIdx: 14, // clinic에서 기본 스캔모드는 Digital impression scan으로 설정
      },
    };

    if (loadedSource === 'server') {
      ProjectActions.edit_project_request({
        projectCode,
        data: {
          ...submitData,
        },
      });
    }
    if (loadedSource === 'local') {
      ProjectActions.edit_project_request({
        channel: 'updateProject',
        data: { projectCode, ...submitData },
      });
    }
  };

  const handleCancelIndication = () => {
    setIsIndicationOpen(false);
    createProjectIndicationStoreReset();
  };

  const handleCopyTeeth = (copyDataList: ITooth[]) => {
    copyDataList.map(copyData => {
      const isExistTooth = teeth.find(
        toothData => toothData.number === antagonistMapper[copyData?.number],
      );

      // 대합치 설정하는 자리에 이미 치아가 있다면 생성하지 않음
      if (!isExistTooth) {
        const newTooth = {
          number: antagonistMapper[copyData?.number],
          indicationIdx: '27',
          material: '22',
          implantType: 7,
          implantAbutmentMaterialIdx: null,
          implantScanbodyScanIdx: null,
          preparationType: 7,
          reconstructionType: '27',
          materialShade: 0,
          isLast: true,
          description: '',
          default: null,
          advanced: null,
          hasData: true,
          color: '#FCBA3C',
          indicationAlias: 'ADG',
        };

        setTeeth(prev => [...prev, newTooth]);
      }
    });
  };

  return {
    numbering,
    setNumbering,
    teeth,
    setTeeth,
    tooth,
    setTooth,
    bridge,
    setBridge,
    copyData,
    setCopyData,
    teethContextActions,
    toothSvgRef,
    isValidCreateProject,
    onToggleTooth: handleToggleTooth,
    onChangeToothValue: handleChangeToothValue,
    onNumberingClick: handleNumberingClick,
    onSaveIndication: handleSaveIndication,
    onCancelIndication: handleCancelIndication,
    onCopyTeeth: handleCopyTeeth,
    onToggleToothPhotogrammetry: handleToggleToothPhotogrammetry,
  };
};
